// The table's HTTP surface as the client uses it (slice 03, issue 743).
// Every call goes to this origin with the session's bearer; a 404 is a table
// that is not ours to see, indistinguishable from none at all.
import type { Power, ProjectedState, TableView } from '@dragons-table/vtt-shared';
import { authFetch } from './session';

export type TableResult = { kind: 'ok'; view: TableView } | { kind: 'not-found' } | { kind: 'refused'; error: string; reason?: string } | { kind: 'error' };

/**
 * What a refusal says to a person: the server's own words when it gave them, else the code put in words — never the
 * bare code (the review of 2026-10-10: the Scenes panel showed "bad_request").
 */
export function refusalWords(r: { error: string; reason?: string }): string {
  if (r.reason) return r.reason;
  if (r.error === 'locked') return 'The DM has locked your colour.';
  if (r.error === 'not_open') return 'The DM has not opened this table yet.';
  if (r.error === 'forbidden') return 'Only the DM can do that.';
  if (r.error === 'bad_request') return 'That was not accepted: check what you typed.';
  return 'That could not be done right now.';
}

const refusedFrom = (body: unknown, status: number): { kind: 'refused'; error: string; reason?: string } => {
  const { error, reason } = (body ?? {}) as { error?: unknown; reason?: unknown };
  return { kind: 'refused', error: typeof error === 'string' ? error : `http_${status}`, ...(typeof reason === 'string' && reason ? { reason } : {}) };
};

async function call(path: string, init: RequestInit, fetchImpl: typeof fetch): Promise<TableResult> {
  try {
    const res = await authFetch(path, {
      ...init,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...((init.headers ?? {}) as Record<string, string>) },
    }, fetchImpl);
    if (res.status === 404) return { kind: 'not-found' };
    const body: unknown = await res.json().catch(() => null);
    if (!res.ok) return refusedFrom(body, res.status);
    return { kind: 'ok', view: body as TableView };
  } catch {
    return { kind: 'error' };
  }
}

export const fetchTable = (campaignId: string, fetchImpl: typeof fetch = fetch) =>
  call(`/api/tables/${campaignId}`, { method: 'GET' }, fetchImpl);
export const openTable = (campaignId: string, fetchImpl: typeof fetch = fetch) =>
  call(`/api/tables/${campaignId}/open`, { method: 'POST' }, fetchImpl);
/** The practice table (qa-runs 1163): opened with its ready-made scene, and put back as it started. */
export const openPractice = (campaignId: string, fetchImpl: typeof fetch = fetch) =>
  call(`/api/tables/${campaignId}/practice/open`, { method: 'POST' }, fetchImpl);
export const resetPractice = (campaignId: string, fetchImpl: typeof fetch = fetch) =>
  call(`/api/tables/${campaignId}/practice/reset`, { method: 'POST' }, fetchImpl);
export const setMyColour = (campaignId: string, colour: string, fetchImpl: typeof fetch = fetch) =>
  call(`/api/tables/${campaignId}/me/colour`, { method: 'PUT', body: JSON.stringify({ colour }) }, fetchImpl);
export const setMemberColour = (campaignId: string, userId: string, colour: string | null, locked: boolean, fetchImpl: typeof fetch = fetch) =>
  call(`/api/tables/${campaignId}/members/${userId}/colour`, { method: 'PUT', body: JSON.stringify({ colour, locked }) }, fetchImpl);
export const setBaseSystem = (campaignId: string, baseSystem: string | null, fetchImpl: typeof fetch = fetch) =>
  call(`/api/tables/${campaignId}/base-system`, { method: 'PUT', body: JSON.stringify({ base_system: baseSystem }) }, fetchImpl);

// Scenes (issue 834): the DM's. Each answers the DM's table view, the scene list in it.
export async function createScene(campaignId: string, name: string, fetchImpl: typeof fetch = fetch): Promise<TableResult & { sceneId?: string }> {
  try {
    const res = await authFetch(`/api/tables/${campaignId}/scenes`, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) }, fetchImpl);
    const body = (await res.json().catch(() => null)) as { scene?: { id: string }; view?: TableView; error?: string; reason?: string } | null;
    if (res.status === 404) return { kind: 'not-found' };
    if (!res.ok || !body?.view) return refusedFrom(body, res.status);
    return { kind: 'ok', view: body.view, sceneId: body.scene?.id };
  } catch {
    return { kind: 'error' };
  }
}
export const renameScene = (campaignId: string, sceneId: string, name: string, fetchImpl: typeof fetch = fetch) =>
  call(`/api/tables/${campaignId}/scenes/${sceneId}`, { method: 'PUT', body: JSON.stringify({ name }) }, fetchImpl);
export const deleteScene = (campaignId: string, sceneId: string, fetchImpl: typeof fetch = fetch) =>
  call(`/api/tables/${campaignId}/scenes/${sceneId}`, { method: 'DELETE' }, fetchImpl);
export const goLive = (campaignId: string, sceneId: string, fetchImpl: typeof fetch = fetch) =>
  call(`/api/tables/${campaignId}/live`, { method: 'PUT', body: JSON.stringify({ scene_id: sceneId }) }, fetchImpl);

/** The table as a specific person has it (slice 09; design/11 §6): the DM's ask, answered with that person's own projection, or null when it cannot be. */
export async function fetchViewAs(campaignId: string, userId: string, fetchImpl: typeof fetch = fetch): Promise<{ viewer: { id: string; role: 'dm' | 'player' }; state: ProjectedState } | null> {
  try {
    const res = await authFetch(`/api/tables/${campaignId}/view-as/${userId}`, { headers: { Accept: 'application/json' } }, fetchImpl);
    if (!res.ok) return null;
    return (await res.json()) as { viewer: { id: string; role: 'dm' | 'player' }; state: ProjectedState };
  } catch {
    return null;
  }
}

// ── Watching (issue 1040; COMM-009) ─────────────────────────────────────────
// A watch link is this origin's `/t/<campaignId>?watch=<token>`. The sign-in hop through the site's door drops the query,
// so the token is kept in this tab's sessionStorage until the session exists and the seat is claimed.
const watchKey = (campaignId: string) => `vtt.watch.${campaignId}`;
const WATCH_TOKEN_RE = /^[A-Za-z0-9_-]{32,128}$/;

/** Keeps a `?watch=` token from the address for this table, and strips it from the address bar. */
export function keepWatchToken(campaignId: string, loc: Location = window.location, hist: History = window.history): void {
  const params = new URLSearchParams(loc.search);
  const token = params.get('watch');
  if (token === null) return;
  params.delete('watch');
  const rest = params.toString();
  hist.replaceState(null, '', loc.pathname + (rest ? `?${rest}` : '') + loc.hash);
  if (!WATCH_TOKEN_RE.test(token)) return;
  try { sessionStorage.setItem(watchKey(campaignId), token); } catch { /* storage blocked: the link works once signed in */ }
}

/** The kept token for this table, taken (removed) as it is read. */
export function takeWatchToken(campaignId: string): string | null {
  try {
    const token = sessionStorage.getItem(watchKey(campaignId));
    sessionStorage.removeItem(watchKey(campaignId));
    return token;
  } catch {
    return null;
  }
}

/** Claims a seat to watch; true when the link was live (or the person is already at the table). */
export async function claimWatch(campaignId: string, token: string, fetchImpl: typeof fetch = fetch): Promise<boolean> {
  try {
    const res = await authFetch(`/api/tables/${campaignId}/watch`, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) }, fetchImpl);
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * An old party address (issue 1578): a bookmark, invitation or notification that named a party's table. The campaign
 * whose table it is, for someone who may open it or who holds a live invitation to watch it; null for anything else,
 * read as today's not-found.
 */
const CAMPAIGN_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export async function tableOfParty(partyId: string, watch: string | null = null, fetchImpl: typeof fetch = fetch): Promise<string | null> {
  try {
    const q = watch ? `?watch=${encodeURIComponent(watch)}` : '';
    const res = await authFetch(`/api/tables/by-party/${encodeURIComponent(partyId)}${q}`, { headers: { Accept: 'application/json' } }, fetchImpl);
    if (!res.ok) return null;
    const body = (await res.json()) as { campaignId?: unknown };
    return typeof body.campaignId === 'string' && CAMPAIGN_RE.test(body.campaignId) ? body.campaignId : null;
  } catch {
    return null;
  }
}

export interface WatchInvite { id: string; created: string; watching: number; revoked: boolean }

/** The DM's invites, or null when they could not be read. */
export async function listWatchInvites(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<WatchInvite[] | null> {
  try {
    const res = await authFetch(`/api/tables/${campaignId}/watch-invites`, { headers: { Accept: 'application/json' } }, fetchImpl);
    if (!res.ok) return null;
    return ((await res.json()) as { invites: WatchInvite[] }).invites;
  } catch {
    return null;
  }
}

/** A new invite: its id and the whole link to hand out, or the refusal's words. */
export async function createWatchInvite(campaignId: string, fetchImpl: typeof fetch = fetch, origin: string = window.location.origin): Promise<{ id: string; url: string } | { refused: string }> {
  try {
    const res = await authFetch(`/api/tables/${campaignId}/watch-invites`, { method: 'POST', headers: { Accept: 'application/json' } }, fetchImpl);
    const body = (await res.json().catch(() => null)) as { id?: string; url?: string; reason?: string } | null;
    if (!res.ok || !body?.id || !body.url) return { refused: body?.reason ?? 'The invite could not be made just now.' };
    return { id: body.id, url: `${origin}${body.url}` };
  } catch {
    return { refused: 'The invite could not be made just now.' };
  }
}

/** A campaign GM the DM may name co-DM (issue 1159), as `GET /api/tables/:campaignId/co-dms` lists them. */
export interface CoDmCandidate {
  id: string; handle: string | null; displayName: string | null; seated: boolean; powers: Power[] | null;
  /** Running this session for the DM (issue 1160), with whether the DM's hidden rolls went across. */
  guest?: { hiddenRolls: boolean } | null;
}

/**
 * Hand the session to a guest DM (issue 1160): the tokens and the hidden monsters always go across; the DM chooses the
 * notes and their hidden rolls. The list again, or the server's refusal in words.
 */
export async function handSession(campaignId: string, userId: string, choices: { notes: boolean; hiddenRolls: boolean }, fetchImpl: typeof fetch = fetch): Promise<{ candidates: CoDmCandidate[] } | { refused: string }> {
  try {
    const res = await authFetch(`/api/tables/${campaignId}/guest-dm`, { method: 'PUT', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ userId, notes: choices.notes, hiddenRolls: choices.hiddenRolls }) }, fetchImpl);
    const body = (await res.json().catch(() => null)) as { candidates?: CoDmCandidate[]; reason?: string } | null;
    if (res.ok && body?.candidates) return { candidates: body.candidates };
    return { refused: body?.reason ?? 'The session could not be handed over just now.' };
  } catch {
    return { refused: 'The session could not be handed over just now.' };
  }
}

/** Take the table back from the guest DM (issue 1160): the list again, or null. */
export async function takeTableBack(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<CoDmCandidate[] | null> {
  try {
    const res = await authFetch(`/api/tables/${campaignId}/guest-dm`, { method: 'DELETE', headers: { Accept: 'application/json' } }, fetchImpl);
    if (!res.ok) return null;
    return ((await res.json()) as { candidates: CoDmCandidate[] }).candidates;
  } catch {
    return null;
  }
}

export async function listCoDms(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<CoDmCandidate[] | null> {
  try {
    const res = await authFetch(`/api/tables/${campaignId}/co-dms`, { headers: { Accept: 'application/json' } }, fetchImpl);
    if (!res.ok) return null;
    return ((await res.json()) as { candidates: CoDmCandidate[] }).candidates;
  } catch {
    return null;
  }
}

/** Name a co-DM, or change their powers: the list again, or the server's refusal in words. */
export async function nameCoDm(campaignId: string, userId: string, powers: Power[], fetchImpl: typeof fetch = fetch): Promise<{ candidates: CoDmCandidate[] } | { refused: string }> {
  try {
    const res = await authFetch(`/api/tables/${campaignId}/co-dms/${userId}`, { method: 'PUT', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ powers }) }, fetchImpl);
    const body = (await res.json().catch(() => null)) as { candidates?: CoDmCandidate[]; reason?: string } | null;
    if (res.ok && body?.candidates) return { candidates: body.candidates };
    return { refused: body?.reason ?? 'That could not be saved just now.' };
  } catch {
    return { refused: 'That could not be saved just now.' };
  }
}

export async function unnameCoDm(campaignId: string, userId: string, fetchImpl: typeof fetch = fetch): Promise<CoDmCandidate[] | null> {
  try {
    const res = await authFetch(`/api/tables/${campaignId}/co-dms/${userId}`, { method: 'DELETE', headers: { Accept: 'application/json' } }, fetchImpl);
    if (!res.ok) return null;
    return ((await res.json()) as { candidates: CoDmCandidate[] }).candidates;
  } catch {
    return null;
  }
}

export async function revokeWatchInvite(campaignId: string, inviteId: string, fetchImpl: typeof fetch = fetch): Promise<WatchInvite[] | null> {
  try {
    const res = await authFetch(`/api/tables/${campaignId}/watch-invites/${inviteId}`, { method: 'DELETE', headers: { Accept: 'application/json' } }, fetchImpl);
    if (!res.ok) return null;
    return ((await res.json()) as { invites: WatchInvite[] }).invites;
  } catch {
    return null;
  }
}
